/* Blog Notification Drawer (Bell) - STICKY HEADER SAFE (no hanging after scroll) */
(() => {
  const OPEN_SEL = '[data-blog-notif-open]';
  const DRAWER_ID_FALLBACK = 'blog-notif-drawer';
  const LOCK_CLASS = 'blog-notif-lock';

  let activeDrawer = null;
  let activeButton = null;

  // store original drawer position so we can restore after close
  const restoreMap = new WeakMap();

  /* ---------- Scroll lock (safe) ---------- */
  function lockScroll() {
    if (document.documentElement.classList.contains(LOCK_CLASS)) return;

    const y = window.scrollY || 0;
    document.documentElement.classList.add(LOCK_CLASS);
    document.documentElement.setAttribute('data-blog-notif-locked', '1');

    // lock without “jump”
    document.body.style.position = 'fixed';
    document.body.style.top = `-${y}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function unlockScroll() {
    if (!document.documentElement.classList.contains(LOCK_CLASS)) return;
    if (document.documentElement.getAttribute('data-blog-notif-locked') !== '1') return;

    const top = document.body.style.top || '0';
    const y = Math.abs(parseInt(top, 10)) || 0;

    document.documentElement.classList.remove(LOCK_CLASS);
    document.documentElement.removeAttribute('data-blog-notif-locked');

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';

    window.scrollTo(0, y);
  }

  /* ---------- Drawer relocation (fix sticky header transform issue) ---------- */
  function ensureDrawerInBody(drawer) {
    if (!drawer) return;

    // already moved
    if (drawer.dataset.blogNotifMoved === '1') return;

    // save original location
    restoreMap.set(drawer, {
      parent: drawer.parentNode,
      next: drawer.nextSibling
    });

    // move to body to avoid transform/overflow clipping from sticky header wrappers
    document.body.appendChild(drawer);
    drawer.dataset.blogNotifMoved = '1';
  }

  function restoreDrawer(drawer) {
    if (!drawer) return;

    const info = restoreMap.get(drawer);
    if (!info || !info.parent) return; // parent might not exist after section reload

    try {
      if (info.next && info.next.parentNode === info.parent) {
        info.parent.insertBefore(drawer, info.next);
      } else {
        info.parent.appendChild(drawer);
      }
    } catch (_) {
      // if restore fails, keep in body (still works)
    }

    drawer.dataset.blogNotifMoved = '0';
  }

  /* ---------- Helpers ---------- */
  function getDrawerFromButton(btn) {
    const id = btn?.getAttribute('aria-controls') || DRAWER_ID_FALLBACK;
    return document.getElementById(id) || document.getElementById(DRAWER_ID_FALLBACK);
  }

  function setExpanded(btn, val) {
    if (!btn) return;
    btn.setAttribute('aria-expanded', val ? 'true' : 'false');
  }

  function isOpen() {
    return !!(activeDrawer && activeDrawer.classList.contains('is-open'));
  }

  /* ---------- Badge ---------- */
  function updateBadges(root = document) {
    root.querySelectorAll(OPEN_SEL).forEach((btn) => {
      const badge = btn.querySelector('.blog-notif-badge');
      if (!badge) return;

      const total = parseInt(btn.dataset.blogTotal || '0', 10);

      // hide zero
      if (Number.isFinite(total) && total > 0) {
        badge.textContent = total;
        badge.style.display = 'flex';
      } else {
        badge.textContent = '';
        badge.style.display = 'none';
      }
    });
  }

  /* ---------- Feed render (optional) ---------- */
  async function hydrateDrawer(drawer, btn) {
    const list = drawer.querySelector('[data-blog-notif-list]');
    if (!list) return;
    if (list.getAttribute('data-loaded') === '1') return;

    const handle = btn.dataset.blogHandle || 'news';
    const limit = parseInt(btn.dataset.blogLimit || '6', 10) || 6;

    list.innerHTML = `<div class="blog-notif-empty">Loading…</div>`;

    const res = await fetch(`/blogs/${encodeURIComponent(handle)}.atom`, {
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!res.ok) throw new Error(`Feed HTTP ${res.status}`);

    const xmlText = await res.text();
    const xml = new DOMParser().parseFromString(xmlText, 'text/xml');
    const entries = Array.from(xml.getElementsByTagName('entry')).slice(0, limit);

    if (!entries.length) {
      list.innerHTML = `<div class="blog-notif-empty">No posts found.</div>`;
      list.setAttribute('data-loaded', '1');
      return;
    }

    const cards = entries
      .map((entry) => {
        const title = (entry.getElementsByTagName('title')[0]?.textContent || '').trim();
        const linkEl = entry.querySelector('link[rel="alternate"]') || entry.querySelector('link');
        const href = linkEl?.getAttribute('href') || '#';

        const contentHtml =
          (entry.getElementsByTagName('content')[0]?.textContent || '').trim() ||
          (entry.getElementsByTagName('summary')[0]?.textContent || '').trim();

        const { img, excerpt } = extractFromHtml(contentHtml);

        return `
          <article class="blog-notif-card">
            ${img ? `<div class="blog-notif-card__media"><img src="${esc(img)}" alt="${esc(title)}" loading="lazy"></div>` : ''}
            <a class="blog-notif-card__heading" href="${esc(href)}">${esc(title)}</a>
            ${excerpt ? `<div class="blog-notif-card__excerpt">${esc(excerpt)}</div>` : ''}
            <a class="blog-notif-card__btn" href="${esc(href)}">View More</a>
          </article>
        `;
      })
      .join('');

    list.innerHTML =
      cards + `<a class="blog-notif-viewall" href="/blogs/${encodeURIComponent(handle)}">View all posts</a>`;

    list.setAttribute('data-loaded', '1');
  }

  function extractFromHtml(html) {
    let img = '';
    let excerpt = '';

    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const imgEl = doc.querySelector('img');
      if (imgEl) img = imgEl.getAttribute('src') || '';
      const text = (doc.body?.textContent || '').replace(/\s+/g, ' ').trim();
      excerpt = text.length > 150 ? text.slice(0, 150).trim() + '…' : text;
    } catch (_) {
      const text = String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      excerpt = text.length > 150 ? text.slice(0, 150).trim() + '…' : text;
    }

    return { img, excerpt };
  }

  function esc(str) {
    return String(str).replace(/[&<>"']/g, (s) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[s])
    );
  }

  /* ---------- Open / Close ---------- */
  function openDrawer(btn) {
    const drawer = getDrawerFromButton(btn);
    if (!drawer) return; // never lock if drawer missing

    // IMPORTANT FIX: move drawer to body so sticky header transforms can't clip it
    ensureDrawerInBody(drawer);

    activeDrawer = drawer;
    activeButton = btn;

    setExpanded(btn, true);
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');

    // lock after open
    lockScroll();

    // if still not visible (CSS conflict), instantly rollback lock
    requestAnimationFrame(() => {
      const st = window.getComputedStyle(drawer);
      const visible = st.display !== 'none' && st.visibility !== 'hidden' && st.opacity !== '0';
      if (!visible) {
        // rollback
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        setExpanded(btn, false);
        unlockScroll();
        activeDrawer = null;
        activeButton = null;
      }
    });

    // focus panel
    const panel = drawer.querySelector('.blog-notif-drawer__panel') || drawer;
    requestAnimationFrame(() => {
      try {
        panel.setAttribute('tabindex', '-1');
        panel.focus({ preventScroll: true });
      } catch (_) {}
    });

    hydrateDrawer(drawer, btn).catch(() => {});
  }

  function closeDrawer() {
    try {
      if (activeDrawer) {
        activeDrawer.classList.remove('is-open');
        activeDrawer.setAttribute('aria-hidden', 'true');

        // restore drawer back to original place (optional)
        restoreDrawer(activeDrawer);
      }
      if (activeButton) setExpanded(activeButton, false);
    } finally {
      unlockScroll();
      try {
        activeButton?.focus({ preventScroll: true });
      } catch (_) {}
      activeDrawer = null;
      activeButton = null;
    }
  }

  /* ---------- Events (delegation = works even after sticky header swaps) ---------- */
  function onDocClick(e) {
    const btn = e.target.closest(OPEN_SEL);
    if (btn) {
      e.preventDefault();
      if (isOpen() && activeButton === btn) closeDrawer();
      else {
        if (isOpen()) closeDrawer();
        openDrawer(btn);
      }
      return;
    }

    if (!isOpen()) return;

    // close triggers
    const clickedOverlay = e.target.closest('.blog-notif-drawer__overlay');
    const clickedClose =
      e.target.closest('[data-blog-notif-close]') ||
      e.target.closest('.blog-notif-drawer__close') ||
      e.target.closest('.blog-notif-close');

    if (clickedOverlay || clickedClose) {
      e.preventDefault();
      closeDrawer();
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape' && isOpen()) {
      e.preventDefault();
      closeDrawer();
    }
  }

  function init() {
    updateBadges();
    const d = document.getElementById(DRAWER_ID_FALLBACK);
    if (d && !d.classList.contains('is-open')) d.setAttribute('aria-hidden', 'true');
  }

  document.addEventListener('click', onDocClick, { passive: false });
  document.addEventListener('keydown', onKeydown);

  window.addEventListener('pagehide', () => {
    // safety: never leave page locked
    unlockScroll();
  });

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
